import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { ModelReviewSource } from '../../../types';
import { SOURCE_LABELS, formatCheckedAgo } from './reviewUtils';

/**
 * Source health strip (task 1093): one chip per review source — Anthropic's
 * models list, OpenAI's models list, the models.dev catalog — with its status,
 * model counts and when it was last fetched, plus Refresh. A stale or
 * unavailable source adds a one-line banner naming the columns it affects.
 */

const DOT: Record<ModelReviewSource['status'], string> = {
  ok: 'bg-green-500',
  stale: 'bg-amber-500',
  unavailable: 'bg-red-500',
};

const CHIP: Record<ModelReviewSource['status'], string> = {
  ok: 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
  stale: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40',
  unavailable: 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40',
};

function counts(source: ModelReviewSource): string {
  if (source.status === 'unavailable' && !source.fetchedAt) return 'unavailable';
  if (source.id === 'openai' && typeof source.chatModelCount === 'number') {
    return `${source.modelCount.toLocaleString('en-US')} models · ${source.chatModelCount.toLocaleString('en-US')} chat`;
  }
  return `${source.modelCount.toLocaleString('en-US')} models`;
}

/** What a degraded source takes away from the page, in one line. */
function bannerFor(source: ModelReviewSource, now: number): string | null {
  if (source.status === 'ok') return null;
  const label = SOURCE_LABELS[source.id] ?? source.id;
  const state =
    source.status === 'stale'
      ? `is stale (the refresh failed${source.error ? `: ${source.error}` : ''}; showing the copy fetched ${formatCheckedAgo(source.fetchedAt, now)})`
      : `is unavailable${source.error ? ` (${source.error})` : ''}`;
  switch (source.id) {
    case 'anthropic':
      return `${label} ${state}. Affects Anthropic limits (context window, max output); ${
        source.status === 'unavailable' ? 'Anthropic rows show as Unverified.' : 'values may be out of date.'
      }`;
    case 'openai':
      return `${label} ${state}. Affects OpenAI limits and shutdown dates; ${
        source.status === 'unavailable' ? 'OpenAI rows show as Unverified.' : 'values may be out of date.'
      }`;
    case 'models-dev':
      return `${label} ${state}. Affects pricing (input, output, cache, tiers) and OpenAI limits; ${
        source.status === 'unavailable'
          ? 'those cells show no provider value and "Add to catalog" is paused until the next successful refresh.'
          : 'suggested values may be out of date.'
      }`;
    default:
      return `${label} ${state}.`;
  }
}

interface ReviewSourceStripProps {
  sources: ModelReviewSource[];
  onRefresh: () => void;
  refreshing: boolean;
  now?: number;
}

export function ReviewSourceStrip({ sources, onRefresh, refreshing, now = Date.now() }: ReviewSourceStripProps) {
  const banners = sources.map((s) => ({ id: s.id, status: s.status, text: bannerFor(s, now) })).filter((b) => b.text);

  return (
    <section aria-label="Sources" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Sources</span>
        {sources.map((source) => (
          <div
            key={source.id}
            data-testid={`review-source-${source.id}`}
            data-status={source.status}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${CHIP[source.status]}`}
          >
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${DOT[source.status]}`} />
            <span className="font-medium text-gray-900 dark:text-white">{SOURCE_LABELS[source.id] ?? source.id}</span>
            <span className="text-gray-600 dark:text-gray-300">{counts(source)}</span>
            {source.status !== 'ok' && (
              <span className={source.status === 'stale' ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300'}>
                {source.status}
              </span>
            )}
            <span className="text-gray-500 dark:text-gray-400">checked {formatCheckedAgo(source.fetchedAt, now)}</span>
          </div>
        ))}
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      {banners.map((banner) => (
        <p
          key={banner.id}
          role="status"
          data-testid={`review-source-banner-${banner.id}`}
          className={`rounded-md px-3 py-2 text-sm ${
            banner.status === 'stale'
              ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
              : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'
          }`}
        >
          {banner.text}
        </p>
      ))}
    </section>
  );
}
